export const createODataQuery = (
  searchableFields,
  betweenFields = [],
  rawQuery = "",
  searchString,
  sortModel = null,
  pageSize,
  currentPage,
  pagination = true,
  orderCol = ""
) => {
  let query;
  if (!pagination) {
    query = ["$count=true"];
  } else {
    query = [`$top=${pageSize}`, `$skip=${pageSize * currentPage}`, "$count=true"];
  }

  let filter = createODataFilter(searchableFields, betweenFields, searchString, rawQuery);
  if (filter !== "") {
    query.push(`$filter=${filter}`);
  }

  let orderBy = "";
  if (sortModel?.orderCol) {
    orderBy = `$orderby=${sortModel.orderCol} ${sortModel.orderByAscending ? "asc" : "desc"}`;
  } else if (orderCol.length > 0) {
    orderBy = `$orderby=${orderCol}`;
  }
  if (orderBy !== "") {
    query.push(orderBy);
  }
  return "?" + query.join("&");
};

// export const createODataFieldQuery = (searchableFields, sortModel, pageSize, currentPage) => {
//   let filter = createODataFilter(searchableFields, searchString);
//   let orderBy = sortModel?.orderCol ? `$orderBy=${sortModel.orderCol} ${sortModel.orderByAscending ? "asc" : "desc"}&` : "";

//   return `?${filter}$top=${pageSize}&$skip=${pageSize * currentPage}&${orderBy}$count=true`;
// };

export const createODataFilter = (searchableFields, betweenFields, searchString, query) => {
  let filter = [createODataEqualityFilter(searchableFields, searchString), createODataBetweenFilter(betweenFields), createODataRawFilter(query)];
  filter = filter.filter(f => f.length > 0);

  return filter.length === 0 ? "" : filter.join(" and ");
};

export const createODataRawFilter = query => {
  if (typeof query === "string") {
    return query.length === 0 ? "" : query;
  }

  if (typeof query === "object") {
    let queryParts = query.filter(f => f.length > 0);
    return query.length === 0 ? "" : queryParts.join(" and ");
  }
};

// export const createODataFieldQuery = (searchableFields, betweenFields, sortModel, pageSize, currentPage) => {
//   let filter = [createODataEqualityFilter(searchableFields, searchString), createODataBetweenFilter(betweenFields)];
//   let orderBy = sortModel?.orderCol ? `$orderBy=${sortModel.orderCol} ${sortModel.orderByAscending ? "asc" : "desc"}&` : "";

//   return `?$filter=${filter.join(" and ")}$top=${pageSize}&$skip=${pageSize * currentPage}&${orderBy}$count=true`;
// };

// export const createODataFilter = (searchableFields, searchString) => {
//   return searchString ? `$filter=${searchableFields.map(field => `contains(tolower(${field}), '${searchString.toLowerCase()}')`).join(" or ")}` : "";
// };

export const createODataEqualityFilter = (searchableFields, searchString) => {
  return searchString ? `(${searchableFields.map(field => `contains(tolower(${field}), '${searchString.toLowerCase()}')`).join(" or ")})` : "";
};

export const createODataBetweenFilter = betweenFields => {
  return betweenFields
    ? `${betweenFields.map(between => `${between.field} ge ${between.startDate} and ${between.field} le ${between.endDate} `)}`
    : "";
};

/**
 * Safely convert value to lowercase string, handling non-string types
 * @param {*} value - The value to convert
 * @returns {string} Lowercase string representation
 */
const safeToLowerCase = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value).toLowerCase();
};

/**
 * Escape string value for OData queries to prevent injection attacks
 * @param {string} value - The string value to escape
 * @returns {string} Escaped string safe for OData queries
 */
const escapeODataString = (value) => {
  if (typeof value !== "string") {
    return value;
  }
  // Escape single quotes by doubling them (OData standard)
  return value.replace(/'/g, "''");
};

/**
 * Check if a value is numeric (integer or decimal)
 * @param {*} value - The value to check
 * @returns {boolean} True if the value is numeric
 */
const isNumericValue = (value) => {
  // Empty string, null, undefined should return false
  if (value === "" || value === null || value === undefined) {
    return false;
  }
  // Check if value can be parsed as a valid number
  const num = parseFloat(value);
  return !isNaN(num) && isFinite(num);
};

/**
 * Detect if a field is likely numeric based on field name patterns
 * @param {string} field - The field name
 * @returns {boolean} True if field appears to be numeric
 */
const isNumericField = (field) => {
  // More specific patterns - avoid false positives like phoneNumber
  const numericPatterns = [
    /id$/i,              // employeeId, projectId
    /count$/i,           // itemCount, recordCount
    /length$/i,          // programLengthYears
    /amount$/i,          // paymentAmount, totalAmount
    /price$/i,           // unitPrice, totalPrice
    /rate$/i,            // hourlyRate, taxRate
    /^year$/i,           // year (standalone)
    /years$/i,           // years, programYears
    /quantity$/i,        // quantity, orderQuantity
    /^hours$/i,          // hours (standalone)
    /totalhours$/i,      // totalHours
    /age$/i,             // age, employeeAge
    /weight$/i,          // weight
    /^index$/i           // index
  ];
  
  return numericPatterns.some(pattern => pattern.test(field));
};

/**
 * Build an OData filter expression for string fields
 * @param {string} field - Field name
 * @param {string} operator - Filter operator
 * @param {*} value - Filter value
 * @returns {string} OData filter expression
 */
const buildStringFilter = (field, operator, value) => {
  const escapedValue = escapeODataString(safeToLowerCase(value));
  const lowerField = `tolower(${field})`;
  
  const operators = {
    contains: `contains(${lowerField}, '${escapedValue}')`,
    equals: `${lowerField} eq '${escapedValue}'`,
    startsWith: `startswith(${lowerField}, '${escapedValue}')`,
    endsWith: `endswith(${lowerField}, '${escapedValue}')`,
    '!=': `${lowerField} ne '${escapedValue}'`
  };
  
  return operators[operator] || operators.contains;
};

/**
 * Build an OData filter expression for numeric fields
 * 
 * Note: String-like operators (contains, startsWith, endsWith) are mapped to equality
 * checks for numeric fields because:
 * 1. These operators don't have semantic meaning for numbers
 * 2. Users may still apply them via the UI on numeric columns
 * 3. The most reasonable interpretation is to check for exact match
 * 
 * @param {string} field - Field name
 * @param {string} operator - Filter operator
 * @param {*} value - Filter value
 * @returns {string} OData filter expression
 */
const buildNumericFilter = (field, operator, value) => {
  const numValue = parseFloat(value);
  
  // Safety check: if parseFloat fails, fall back to string comparison
  if (isNaN(numValue)) {
    return buildStringFilter(field, operator, value);
  }
  
  const operators = {
    // String-like operators map to equality for numeric fields (see note above)
    contains: `${field} eq ${numValue}`,
    equals: `${field} eq ${numValue}`,
    startsWith: `${field} eq ${numValue}`,
    endsWith: `${field} eq ${numValue}`,
    // Comparison operators work as expected
    '>': `${field} gt ${numValue}`,
    '>=': `${field} ge ${numValue}`,
    '<': `${field} lt ${numValue}`,
    '<=': `${field} le ${numValue}`,
    '!=': `${field} ne ${numValue}`
  };
  
  return operators[operator] || operators.equals;
};


// Fields that MUST always behave as STRING even if numeric
const FORCE_STRING_FIELDS = ["internalEmployeeId"];

/**
 * Convert MUI DataGrid Pro filter model to OData filter string
 * Supports various filter operators including contains, equals, startsWith, endsWith, etc.
 * All string values are properly escaped to prevent OData injection attacks
 * Automatically detects numeric fields and applies appropriate filtering without tolower()
 * @param {Object} filterModel - MUI DataGrid filter model
 * @returns {string} OData filter string
 */
export const convertMuiFilterToOData = (filterModel) => {
  if (!filterModel || !filterModel.items || filterModel.items.length === 0) {
    return "";
  }

  const filterParts = filterModel.items
    .filter(item => item.value !== undefined && item.value !== null && item.value !== "")
    .map(item => {
      const { field, operator, value } = item;
      
      // Handle boolean values
      if (typeof value === "boolean") {
        return operator === "!=" 
          ? `${field} ne ${value}` 
          : `${field} eq ${value}`;
      }
      
      // Handle special operators that don't depend on type
      if (operator === "isEmpty") {
        return `${field} eq null or ${field} eq ''`;
      }
      if (operator === "isNotEmpty") {
        return `${field} ne null and ${field} ne ''`;
      }
      
      // Handle isAnyOf operator
      if (operator === "isAnyOf" && Array.isArray(value) && value.length > 0) {
        const isForcedString = FORCE_STRING_FIELDS.includes(field);
        const isNumeric = !isForcedString && (isNumericValue(value[0]) || isNumericField(field));
        const conditions = isNumeric
          ? value.map(v => `${field} eq ${parseFloat(v)}`).join(" or ")
          : value.map(v => `tolower(${field}) eq '${escapeODataString(safeToLowerCase(v))}'`).join(" or ");
        return `(${conditions})`;
      }

      if (FORCE_STRING_FIELDS.includes(field)) {
        return buildStringFilter(field, operator, value);
      }
      
      // Determine if this is a numeric field/value
      const isNumeric = isNumericValue(value) || isNumericField(field);
      
      // Build appropriate filter based on type
      return isNumeric 
        ? buildNumericFilter(field, operator, value)
        : buildStringFilter(field, operator, value);
    })
    .filter(part => part !== "");

  // Handle linkOperator (and/or)
  const linkOperator = filterModel.linkOperator === "or" ? " or " : " and ";
  return filterParts.length > 0 ? `(${filterParts.join(linkOperator)})` : "";
};